import {Component, HostListener, Input, OnInit, signal} from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { CommonDirectives } from '@app/shared/directives/common-directives';
import { AccountTypes } from '@app/shared/enums/accountTypes.enum';
import {Subscription, take} from 'rxjs';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {rxResource} from "@angular/core/rxjs-interop";
import {appointmentType} from "@app/shared/components/generic-dashboard/generic-dashboard.component";
import {NgAppointmentDetails} from "@app/shared/interfaces/appointment.interface";
import {WorkCompAppointmentsService} from "@app/features/workCompDashboard/services/work-comp-appointments.service";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {AppointmentDetailComponent} from "@app/shared/components/appointment-detail/appointment-detail.component";
import {map} from "rxjs/operators";
import {DateFormatPipe} from "@app/shared/pipes/dateFormat.pipe";
import {
  MatExpansionPanel,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {UserRoles} from "@app/core/enums/userRoles.enum";
import {TokenStorageService} from "@app/core/services/token-storage.service";
import {
  CollapsibleComponent
} from "@app/layout/components/simple-collapsible/simple-collapsible/simple-collapsible.component";


export interface AppointmentDetailData {
  appointmentId: string | null;
  patientId: string | null;
  caseNumber: string | null;
  appointmentType: appointmentType
}

@Component({
  selector: 'app-appointment-detail-modal',
  imports: [
    MatDialogModule,
    MatIconModule,
    CommonDirectives,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinner,
    AppointmentDetailComponent,
    DateFormatPipe,
    CollapsibleComponent,
  ],
  templateUrl: './appointment-detail-modal.component.html',
  styleUrl: './appointment-detail-modal.component.scss'
})
export class AppointmentDetailModalComponent implements OnInit {
  @Input() appointmentDetailParams!: any

  userAccountType!: AccountTypes;
  readOnlyMode = true;
  loading = false;
  isWcUser = false;
  personalInfoForm!: FormGroup;
  cpInfoForm!: FormGroup;
  firstClinicCtrl!: FormControl;
  firstProvidersCtrl!: FormControl;
  secondClinicCtrl!: FormControl;
  secondProvidersCtrl!: FormControl;
  subscriptions: Subscription = new Subscription();

  detailSignal = signal<AppointmentDetailData | undefined>(undefined);
  detailData = rxResource<NgAppointmentDetails, AppointmentDetailData | undefined>({
    params: () => this.detailSignal(),
    stream: (params) => this.workCompAppointmentsService
      .getAppointmentDetails$(
        params.params.patientId ?? '',
        params.params.appointmentType !== appointmentType.PatientSearch ? params.params.appointmentId ?? '' : undefined,
        params.params.caseNumber ?? ''
      )
  });

  constructor(
    private dialogRef: MatDialogRef<AppointmentDetailModalComponent>,
    private dialog: MatDialog,
    private workCompAppointmentsService: WorkCompAppointmentsService,
    private tokenStorageService: TokenStorageService
  ) {}

  ngOnInit(): void {
    this.subscribeToCloseEventSubscriptions();
    this.isWcUser = this.tokenStorageService.getEffectiveUser()?.role === UserRoles.workCompUser;
  }

  get shouldShowDocumentsTable(): boolean {
    if (this.appointmentDetailParams?.showDocumentsTable != null) {
      return this.appointmentDetailParams.showDocumentsTable;
    }

    return this.appointmentDetailParams?.appointmentType !== appointmentType.FutureAppointments;
  }

  handleClose() {
    if (this.anyUnsavedChanges()) {
      this.showUnsavedChangesConfirmDialog(() => {
        this.closeDialog();
      });
    } else {
      this.closeDialog();
    }
  }

  private closeDialog() {
    this.subscriptions.unsubscribe();
    this.dialogRef.close();
  }
  private anyUnsavedChanges(): boolean {
    if (this.isReadOnlyMode()) { return false; }
    if (this.userAccountType !== AccountTypes.REFERRAL_PARTNER) {
      return this.personalInfoForm.dirty;
    } else {
      return this.personalInfoForm.dirty || this.cpInfoForm.dirty;
    }
  }

  private isReadOnlyMode(): boolean {
    return this.readOnlyMode;
  }

  private subscribeToCloseEventSubscriptions() {
    this.subscriptions.add(
      this.dialogRef.backdropClick().subscribe((event) => {
        event.preventDefault();
        this.handleClose();
      })
    );

    this.subscriptions.add(
      this.dialogRef.keydownEvents().subscribe(event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.handleClose();
        }
      })
    );
  }

private showUnsavedChangesConfirmDialog(onConfirm: () => void) {
    const dialog = this.dialog.open(ConfirmDialogComponent, { disableClose: true });
    dialog.componentInstance.title = 'Unsaved Changes';
    dialog.componentInstance.content = 'You have unsaved changes. Are you sure you want to leave this page?';
    dialog.componentInstance.onConfirm
      .pipe(take(1))
      .subscribe(() => onConfirm());
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    if (this.anyUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = ''; // required for modern browsers
      return '';
    }
    return undefined;
  }

  protected readonly appointmentType = appointmentType;
}
